import getConfig from 'next/config';
import * as R from 'ramda';
import React, { ChangeEvent, FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';

import fetchWithCatch from '@/lib/fetch-with-catch';
import getThemedButtonProps from '@/lib/get-themed-button-props';
import getVariablePlainText from '@/lib/get-variable-plain-text';
import useVariables from '@/lib/hooks/use-variables';
import { selectGlobalPageTheme } from '@/store/slices/global';

import NormalButton from '../button/normal';

interface LotteryUploadDropzoneProps {
  onUploaded: (filename: string | null) => void;
}

const LotteryUploadDropzone: FC<LotteryUploadDropzoneProps> = ({
  onUploaded,
}) => {
  const variables = useVariables();
  const { publicRuntimeConfig } = getConfig();
  const [validationError, setValidationError] = useState<string | null>(null);

  const maxSizes = {
    'video/mp4': 250000000,
    default: 50000000,
  } as const;

  const getMaxSizeForFile = (fileType: string): number =>
    fileType === 'video/mp4' ? maxSizes['video/mp4'] : maxSizes.default;

  const getMaxSizeInMB = (fileType: string): number =>
    getMaxSizeForFile(fileType) / 1000000;

  const getFileTypeDisplayName = (fileType: string): string =>
    fileType === 'video/mp4' ? 'MP4' : 'this file type';

  const acceptedTypes = 'image/*, audio/*, .pdf, .mp4';

  const handleFileUpload = async (file: File): Promise<void> => {
    if (file) {
      const dataToSend = new FormData();
      dataToSend.append('file', file);

      const { data, error } = await fetchWithCatch<{ filename: string }>(() =>
        fetch(`${publicRuntimeConfig.lottery2ApiBaseUrl}/files`, {
          method: 'POST',
          body: dataToSend,
        }),
      );

      if (error) console.error(error);

      onUploaded(data?.filename || null);
    }
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    multiple: false,
    validator: (file) => {
      const fileType = file.type;
      const maxSize = getMaxSizeForFile(fileType);

      if (file.size > maxSize) {
        const maxSizeMB = getMaxSizeInMB(fileType);
        const fileTypeName = getFileTypeDisplayName(fileType);

        return {
          code: 'file-too-large',
          message: `Bitte lade eine kleinere Datei hoch. Maximale Größe für ${fileTypeName} ist ${maxSizeMB} MB.`,
        };
      }

      return null;
    },
    accept: acceptedTypes,
    onDropRejected: (rejectedFiles) => {
      const errorMessages = rejectedFiles.map(
        ({ file, errors: fileErrors }) => {
          const fileTypeError = fileErrors.find(
            (error) => error.code === 'file-invalid-type',
          );

          if (fileTypeError) {
            return `Dateityp nicht unterstützt. Erlaubte typen: ${acceptedTypes}`;
          }

          const fileSizeError = fileErrors.find(
            (error) => error.code === 'file-too-large',
          );

          if (fileSizeError) {
            return fileSizeError.message;
          }

          return (
            fileErrors[0]?.message || 'Datei konnte nicht hochgeladen werden.'
          );
        },
      );
      setValidationError(errorMessages[0] || 'Unknown error');
    },
    onDrop: async (droppedFiles) => {
      setValidationError(null);

      if (droppedFiles.length > 0) {
        await handleFileUpload(droppedFiles[0]);
      }
    },
  });

  const pageTheme = useSelector(selectGlobalPageTheme);
  const buttonProps = getThemedButtonProps(pageTheme);

  const onChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];

    if (file) {
      await handleFileUpload(file);
    }
  };

  return (
    <div {...getRootProps({ className: 'dropzone' })}>
      <input {...getInputProps({ onChange })} />

      {validationError && (
        <div className="text-red-600 text-sm mb-2 p-2 bg-red-50 border border-red-200 rounded">
          {validationError}
        </div>
      )}

      {/* TODO: implement upload button states according to the design */}
      {R.isEmpty(acceptedFiles) ? (
        <NormalButton
          text={
            getVariablePlainText(variables['upload-select-file']) ||
            'Select file'
          }
          {...buttonProps}
        />
      ) : (
        <div className="w-full">
          <h4>File:</h4>
          <ul>
            {acceptedFiles.map((file) => (
              <li key={file.name}>{file.name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LotteryUploadDropzone;
